import type { ProductMetadataByLevel, ProductMetadatum, LocationMetadatum } from "../types/metadata";
import { allProductsDatum } from "../graphql/queries/getProductsMetadata";
import { worldGroupDatum } from "../graphql/queries/getLocationsMetadata";
import { locationMembersInclude } from "../types/metadata";
import { unidentifiedServicesCountryId, servicesPartnersGroupId } from "./locationUtils";
import { LocationMetadatumLevel } from "../types/cpEnums";

// ── Product flattening ─────────────────────────────────────────────

/**
 * Flatten hierarchical product metadata into a sorted list suitable for
 * the product dropdown. Items are ordered: "All Products" (sticky),
 * then section → 2-digit → 4-digit → 6-digit in code-ascending order.
 */
export const flattenProductMetadataForDropdown = (
  metadata: ProductMetadataByLevel,
): FlatProductItem[] => {
  const { section, twoDigit, fourDigit, sixDigit } = metadata;
  const items: FlatProductItem[] = [];

  // "All Products" sticky entry
  items.push({
    id: allProductsDatum.productId,
    name_short_en: allProductsDatum.nameShortEn,
    level: allProductsDatum.productLevel,
    isSticky: true,
  });

  const sortByCode = (a: { code?: string | null }, b: { code?: string | null }) => {
    return Number(a.code) - Number(b.code);
  };

  const sortedSections = [...section].sort(sortByCode);
  const sortedTwoDigit = [...twoDigit].sort(sortByCode);
  const sortedFourDigit = [...fourDigit].sort(sortByCode);
  const sortedSixDigit = sixDigit ? [...sixDigit].sort(sortByCode) : [];

  sortedSections.forEach((sectionItem: ProductMetadatum) => {
    items.push({
      id: sectionItem.productId!,
      code: sectionItem.code!,
      name_short_en: sectionItem.nameShortEn!,
      level: sectionItem.productLevel,
      product_section: sectionItem,
    });

    const sectionTwoDigit = sortedTwoDigit.filter(
      (t: ProductMetadatum) => t.parent?.productId === sectionItem.productId,
    );

    sectionTwoDigit.forEach((twoDigitItem: ProductMetadatum) => {
      items.push({
        id: twoDigitItem.productId!,
        code: twoDigitItem.code!,
        name_short_en: twoDigitItem.nameShortEn!,
        level: twoDigitItem.productLevel,
        product_section: sectionItem,
        product_twoDigit: twoDigitItem,
      });

      const twoDigitFourDigit = sortedFourDigit.filter(
        (f: ProductMetadatum) => f.parent?.productId === twoDigitItem.productId,
      );

      twoDigitFourDigit.forEach((fourDigitItem: ProductMetadatum) => {
        items.push({
          id: fourDigitItem.productId!,
          code: fourDigitItem.code!,
          name_short_en: fourDigitItem.nameShortEn!,
          level: fourDigitItem.productLevel,
          product_section: sectionItem,
          product_twoDigit: twoDigitItem,
          product_fourDigit: fourDigitItem,
        });

        if (sortedSixDigit.length > 0) {
          const fourDigitSixDigit = sortedSixDigit.filter(
            (s: ProductMetadatum) => s.parent?.productId === fourDigitItem.productId,
          );

          fourDigitSixDigit.forEach((sixDigitItem: ProductMetadatum) => {
            items.push({
              id: sixDigitItem.productId!,
              code: sixDigitItem.code!,
              name_short_en: sixDigitItem.nameShortEn!,
              level: sixDigitItem.productLevel,
              product_section: sectionItem,
              product_twoDigit: twoDigitItem,
              product_fourDigit: fourDigitItem,
              product_sixDigit: sixDigitItem,
            });
          });
        }
      });
    });
  });

  return items;
};

/** Minimal parent ref shape compatible with controls' ProductParentRef. */
interface ProductParentRef {
  productId: string;
  [key: string]: unknown;
}

/**
 * A flattened product entry. Structurally compatible with the controls
 * package's `ProductItem` type (both share an index signature).
 */
export interface FlatProductItem {
  id: string;
  name_short_en: string;
  code?: string;
  level: number | string;
  product_section?: ProductParentRef;
  product_twoDigit?: ProductParentRef;
  product_fourDigit?: ProductParentRef;
  product_sixDigit?: ProductParentRef;
  isSticky?: boolean;
  [key: string]: unknown;
}

// ── Location flattening ────────────────────────────────────────────

export interface FlattenLocationOptions {
  /** Whether to exclude the Services Partners entry. */
  hideServicesPartners?: boolean;
}

/** Input shape for flattenLocationMetadata — matches what useLocationsMetadata returns. */
export interface LocationMetadataInput {
  countries: LocationMetadatum[];
  regions: LocationMetadatum[];
  subregions: LocationMetadatum[];
}

/**
 * Flatten hierarchical location metadata into a sorted list suitable for
 * the location dropdown. Items are ordered: World (sticky), then
 * region → subregion → country in alphabetical order.
 */
export const flattenLocationMetadata = (
  metadata: LocationMetadataInput,
  options?: FlattenLocationOptions,
): FlatLocationItem[] => {
  const { countries, regions, subregions } = metadata;
  const items: FlatLocationItem[] = [];

  // World sticky entry
  items.push({
    id: worldGroupDatum.groupId,
    name_short_en: worldGroupDatum.groupName,
    name_en: worldGroupDatum.groupName,
    locationLevel: LocationMetadatumLevel.world,
    isSticky: true,
  });

  // Regions (exclude Services Partners group)
  const sortedRegions = [...regions]
    .filter((r) => r.groupId != null && r.groupId !== servicesPartnersGroupId)
    .sort((a, b) => (a.groupName ?? "").localeCompare(b.groupName ?? "", "en-US"));

  sortedRegions.forEach((region) => {
    const regionGroupId = region.groupId!;
    const regionGroupName = region.groupName ?? "";

    items.push({
      id: regionGroupId,
      name_short_en: regionGroupName,
      name_en: regionGroupName,
      locationLevel: LocationMetadatumLevel.region,
      hybrid_level_1: regionGroupId,
    });

    const regionSubregions = subregions
      .filter((sr) => sr.parentId === regionGroupId)
      .sort((a, b) => (a.groupName ?? "").localeCompare(b.groupName ?? "", "en-US"));

    regionSubregions.forEach((subregion) => {
      const subregionGroupId = subregion.groupId!;
      const subregionGroupName = subregion.groupName ?? "";

      items.push({
        id: subregionGroupId,
        name_short_en: subregionGroupName,
        name_en: subregionGroupName,
        locationLevel: LocationMetadatumLevel.subregion,
        hybrid_level_1: regionGroupId,
        hybrid_level_2: subregionGroupId,
      });

      const subregionCountries = countries
        .filter(
          (country) =>
            locationMembersInclude(subregion.members, country.countryId) &&
            String(country.countryId) !== unidentifiedServicesCountryId,
        )
        .sort((a, b) =>
          (a.nameShortEn || "").localeCompare(b.nameShortEn || "", "en-US"),
        );

      subregionCountries.forEach((country) => {
        items.push({
          id: String(country.countryId!),
          name_short_en: country.nameShortEn!,
          name_en: country.nameEn,
          iso3Code: country.iso3Code,
          locationLevel: LocationMetadatumLevel.country,
          hybrid_level_1: regionGroupId,
          hybrid_level_2: subregionGroupId,
          countryId: String(country.countryId),
        });
      });
    });
  });

  // Services Partners at the end (not sticky)
  if (!options?.hideServicesPartners) {
    items.push({
      id: unidentifiedServicesCountryId,
      name_short_en: "Services Partners",
      name_en: "Services Partners",
      iso3Code: "SVP",
      locationLevel: LocationMetadatumLevel.country,
      hybrid_level_1: servicesPartnersGroupId,
      countryId: unidentifiedServicesCountryId,
    });
  }

  return items;
};

/**
 * A flattened location entry. Structurally compatible with the controls
 * package's `LocationItem` type (both share an index signature).
 */
export interface FlatLocationItem {
  id: string;
  name_short_en: string;
  name_en?: string;
  iso3Code?: string;
  locationLevel: string;
  hybrid_level_1?: string;
  hybrid_level_2?: string;
  countryId?: string;
  isSticky?: boolean;
  [key: string]: unknown;
}
